import { defu } from 'defu'
import { useAppConfiguration } from '~/stores/appConfiguration'
import { useWorkspace } from '~/stores/workspace'

const fetchTenantConfig = (signal?: AbortSignal) =>
  $fetch('/api/config', { signal })
type TenantConfig = Awaited<ReturnType<typeof fetchTenantConfig>>

let tenantConfigController: AbortController | null = null
let pendingTenantConfig: Promise<TenantConfig> | null = null

export default defineNuxtRouteMiddleware(async (to, from) => {
  const deprecatedNameRedirects: Record<string, string> = {
    signin: '/auth/login',
    signup: '/auth/login',
    'signup-confirm': '/auth/login',
  }

  const deprecatedPathRedirects: Record<string, string> = {
    '/signin': '/auth/login',
    '/signup': '/auth/login',
    '/signup-confirm': '/auth/login',
  }

  const name = typeof to.name === 'string' ? to.name : undefined
  if (name && deprecatedNameRedirects[name]) {
    return navigateTo(deprecatedNameRedirects[name], { replace: true })
  }
  if (deprecatedPathRedirects[to.path]) {
    return navigateTo(deprecatedPathRedirects[to.path], { replace: true })
  }

  const appConfigStore = useAppConfiguration()
  const cachedConfig = useState<TenantConfig | null>(
    'tenant-config',
    () => null,
  )

  if (!cachedConfig.value) {
    if (!pendingTenantConfig) {
      tenantConfigController = new AbortController()
      pendingTenantConfig = fetchTenantConfig(tenantConfigController.signal).finally(() => {
        tenantConfigController = null
        pendingTenantConfig = null
      })
    }
    try {
      cachedConfig.value = await pendingTenantConfig
    } catch (error) {
      if ((error as Error).name !== 'AbortError') throw error
      return
    }
  }
  const config = cachedConfig.value

  const workspace = useWorkspace()
  if (
    config?.tenant.singleTenantWorkspaceId &&
    workspace.workspaceId !== config?.tenant.singleTenantWorkspaceId
  ) {
    workspace.workspaceId = config.tenant.singleTenantWorkspaceId
  }

  if (import.meta.client) {
    const appConfig = useAppConfig()

    Object.assign(
      appConfig.tenant,
      defu(config?.tenant ?? {}, appConfig.tenant),
    )
    Object.assign(appConfig, {
      host: config?.host,
      version: config?.version,
    })
  }

  if (
    to.path.includes('claim-lab') &&
    appConfigStore.feature_exclusions_claim_lab_v2
  ) {
    const claimLabMatch = to.path.match(/(\/[^/]+\/claim-lab\/[^/]+)\/(\d+)$/)
    if (claimLabMatch) {
      const [, basePath, lastId] = claimLabMatch
      return navigateTo(`${basePath}/v2/${lastId}`, { replace: true })
    }
  }

  // this is masking a significant bug with formkit
  if (
    to.name === 'exclusions-id-edit' &&
    from.name === 'exclusions-id-edit' &&
    !to.redirectedFrom
  ) {
    return navigateTo('/exclusions')
  }

  if (
    to.name === 'claims-id' &&
    to.path.includes('Claim-') &&
    appConfigStore.feature_claim_id_redirect
  ) {
    return navigateTo(to.path.replace('Claim-', ''))
  }
})
